import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';

interface User {
  shop: string;
  scope: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  verifySession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored authentication on app load
    const storedToken = localStorage.getItem('shopify_tally_token');
    const storedUser = localStorage.getItem('shopify_tally_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        
        // Set default authorization header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        
        // Verify the session is still valid
        verifyStoredSession(storedToken, parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        logout();
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyStoredSession = async (storedToken: string, storedUser: User) => {
    try {
      const response = await apiClient.post('/shopify/auth/verify', {
        shop: storedUser.shop
      });

      if (response.data.success) {
        // Update token if a new one was provided
        if (response.data.data.token && response.data.data.token !== storedToken) {
          const newToken = response.data.data.token;
          setToken(newToken);
          localStorage.setItem('shopify_tally_token', newToken);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        }
      } else {
        throw new Error('Session verification failed');
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    
    // Store in localStorage
    localStorage.setItem('shopify_tally_token', newToken);
    localStorage.setItem('shopify_tally_user', JSON.stringify(newUser));
    
    // Set default authorization header
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    setIsLoading(false);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('shopify_tally_token');
    localStorage.removeItem('shopify_tally_user');
    
    // Remove authorization header
    delete apiClient.defaults.headers.common['Authorization'];
    
    setIsLoading(false);
  };

  const verifySession = async (): Promise<boolean> => {
    if (!user || !token) {
      return false;
    }

    try {
      const response = await apiClient.post('/shopify/auth/verify', {
        shop: user.shop
      });

      if (response.data.success) {
        // Update token if a new one was provided
        if (response.data.data.token && response.data.data.token !== token) {
          const newToken = response.data.data.token;
          setToken(newToken);
          localStorage.setItem('shopify_tally_token', newToken);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        }
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      logout();
      return false;
    }
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    verifySession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};